import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@clarix/auth-store';
import { membersApi, type WorkspaceMemberListParams } from './members.api';

/**
 * React Query hooks over the shared workspace membership list. Slug comes from
 * the auth-store singleton (same pattern as `org.query.ts`), so any app can
 * read the directory without re-wiring identity. Success/error feedback is the
 * caller's job — mutations here stay toast-free so each app applies its own
 * copy (Support's global MutationCache already toasts unhandled errors).
 */

const membersRoot = (slug: string | null) => ['members', slug] as const;

/** One server page of members (server-side search/role/status). */
export function useWorkspaceMemberPage(params: WorkspaceMemberListParams = {}) {
  const slug = useAuthStore((s) => s.workspaceSlug);
  return useQuery({
    queryKey: [...membersRoot(slug), 'page', params],
    queryFn: () => membersApi.list(slug as string, params),
    enabled: !!slug,
    staleTime: 30_000,
  });
}

/** The full membership list (paged walk) — for client-side app-access joins. */
export function useWorkspaceMemberDirectory() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  return useQuery({
    queryKey: [...membersRoot(slug), 'directory'],
    queryFn: () => membersApi.listAll(slug as string),
    enabled: !!slug,
    // The walk is up to 20 requests — membership churns slowly, so don't re-run
    // it every minute just because another picker mounted.
    staleTime: 5 * 60_000,
  });
}

/** Pending/expired workspace invites (per-app grants in apps_json). */
export function useWorkspaceInviteList() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  return useQuery({
    queryKey: [...membersRoot(slug), 'invites'],
    queryFn: () => membersApi.invites(slug as string),
    enabled: !!slug,
    staleTime: 60_000,
  });
}

function useInvalidateMembers() {
  const qc = useQueryClient();
  const slug = useAuthStore((s) => s.workspaceSlug);
  return () => qc.invalidateQueries({ queryKey: membersRoot(slug) });
}

/** Grant a member access to an app (optionally with a permission template). */
export function useGrantMemberAppAccess() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (input: {
      userId: number;
      code: string;
      app_base_role?: string;
      permission_template_id?: string;
    }) => membersApi.grantAppAccess(slug as string, input),
    onSuccess: invalidate,
  });
}

/** Revoke a member's access to an app. */
export function useRevokeMemberAppAccess() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (input: { userId: number; code: string }) =>
      membersApi.revokeAppAccess(slug as string, input),
    onSuccess: invalidate,
  });
}

/** Re-send a pending invitation email. */
export function useResendWorkspaceInvite() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (inviteId: string) => membersApi.resendInvite(slug as string, inviteId),
    onSuccess: invalidate,
  });
}

/** Revoke a pending invitation. */
export function useRevokeWorkspaceInvite() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (inviteId: string) => membersApi.revokeInvite(slug as string, inviteId),
    onSuccess: invalidate,
  });
}
